import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { HiOutlineCheck, HiOutlineX, HiOutlineTrash, HiOutlineRefresh, HiOutlineLightBulb } from "react-icons/hi";
import { SearchableSelect } from "../../components/UI";
import {
  PHONE_COUNTRY_OPTIONS,
  suggestPhoneCountries,
  detectPhoneCountry,
} from "../../utils/validation";

const PAGE = 50;

// Fields that can be edited on an invalid row, with the error prefix that
// belongs to each (see validateImportRows in LeadsList).
const EDIT_FIELDS = [
  { key: "name", label: "Name", errorPrefix: "Name:", get: (l) => l.name || "" },
  { key: "rawPhone", label: "Phone", errorPrefix: "Phone", get: (l) => l.rawPhone ?? l.phone ?? "" },
  { key: "rawAlternatePhone", label: "Alternate Phone", errorPrefix: "Alternate", get: (l) => l.rawAlternatePhone ?? l.alternatePhone ?? "" },
  { key: "email", label: "Email", errorPrefix: "Email:", get: (l) => l.email || "" },
];

const fieldHasError = (lead, field) =>
  (lead.errors || []).some((e) =>
    field.key === "rawPhone"
      ? e.startsWith("Phone") || e.startsWith("Duplicate phone")
      : field.key === "rawAlternatePhone"
        ? e.startsWith("Alternate") || e.startsWith("Duplicate alternate")
        : e.startsWith(field.errorPrefix),
  );

/**
 * Import preview split into "Invalid" and "Valid" tabs. Invalid rows can be
 * fixed inline (name / phone / alternate phone / email) or in bulk (set the
 * phone country, clear alternate phone / email, remove rows), then
 * re-validated — fixed rows move to the Valid tab. Rows whose phone fails are
 * offered the countries the number would be valid in (best match first).
 *
 * onRowsChange(rows) must re-validate the rows and return the validated list.
 */
const ImportPreview = ({ rows, onRowsChange }) => {
  const invalidRows = useMemo(() => rows.filter((l) => !l.isValid), [rows]);
  const validRows = useMemo(() => rows.filter((l) => l.isValid), [rows]);

  const [tab, setTab] = useState(invalidRows.length > 0 ? "invalid" : "valid");
  const [selected, setSelected] = useState(() => new Set());
  const [drafts, setDrafts] = useState({}); // { rowNum: { field: value } }
  const [bulkCountry, setBulkCountry] = useState("");
  const [shown, setShown] = useState(PAGE);

  const activeTab = tab === "invalid" && invalidRows.length === 0 ? "valid" : tab;
  const list = activeTab === "invalid" ? invalidRows : validRows;
  const draftCount = Object.keys(drafts).length;
  const selectedInvalid = invalidRows.filter((l) => selected.has(l.rowNum));

  // Countries already present among valid numbers rank first in suggestions
  // (a file with some +971 numbers most likely has more UAE numbers).
  const fileCountries = useMemo(() => {
    const counts = {};
    validRows.forEach((l) => {
      const c = detectPhoneCountry(l.phone);
      if (c) counts[c] = (counts[c] || 0) + 1;
    });
    return Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  }, [validRows]);

  // { rowNum: [{ country, label, formatted }] } for rows with a phone error
  const suggestions = useMemo(() => {
    const map = {};
    invalidRows.forEach((l) => {
      if (!(l.errors || []).some((e) => e.startsWith("Phone:"))) return;
      const list = suggestPhoneCountries(l.rawPhone ?? l.phone, { boost: fileCountries });
      if (list.length > 0) map[l.rowNum] = list;
    });
    return map;
  }, [invalidRows, fileCountries]);

  // Rows grouped by their best-match country, biggest group first
  const suggestionGroups = useMemo(() => {
    const groups = {};
    Object.entries(suggestions).forEach(([rowNum, list]) => {
      const best = list[0];
      if (!groups[best.country]) groups[best.country] = { ...best, rowNums: [] };
      groups[best.country].rowNums.push(Number(rowNum));
    });
    return Object.values(groups).sort((a, b) => b.rowNums.length - a.rowNums.length);
  }, [suggestions]);
  const selectedWithSuggestion = selectedInvalid.filter((l) => suggestions[l.rowNum]);
  const allSelected = invalidRows.length > 0 && selectedInvalid.length === invalidRows.length;

  const switchTab = (next) => {
    setTab(next);
    setShown(PAGE);
  };

  const toggleRow = (rowNum) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(rowNum) ? next.delete(rowNum) : next.add(rowNum);
      return next;
    });

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(invalidRows.map((l) => l.rowNum)));

  const setDraft = (rowNum, key, value) =>
    setDrafts((prev) => ({ ...prev, [rowNum]: { ...prev[rowNum], [key]: value } }));

  // Merge pending inline edits + an optional per-row change, re-validate, and
  // report how many rows became valid.
  const commit = (changeRow = null, removeRowNums = null) => {
    const before = invalidRows.length;
    let next = rows;
    if (removeRowNums) next = next.filter((l) => !removeRowNums.has(l.rowNum));
    next = next.map((l) => {
      const draft = drafts[l.rowNum];
      let row = draft ? { ...l, ...draft } : l;
      if (changeRow) row = changeRow(row) || row;
      return row;
    });
    const validated = onRowsChange(next);
    setDrafts({});
    const stillInvalid = new Set(validated.filter((l) => !l.isValid).map((l) => l.rowNum));
    setSelected((prev) => new Set([...prev].filter((n) => stillInvalid.has(n))));
    const removed = removeRowNums ? removeRowNums.size : 0;
    const fixed = before - removed - stillInvalid.size;
    if (removed > 0) toast.success(`${removed} row(s) removed from this import`);
    if (fixed > 0) toast.success(`${fixed} row(s) fixed and moved to Valid`);
    else if (removed === 0) toast(`${stillInvalid.size} row(s) still need fixing`, { icon: "⚠️" });
  };

  const bulkApply = (fn) => {
    if (selectedInvalid.length === 0) {
      toast.error("Select one or more invalid rows first");
      return;
    }
    const ids = new Set(selectedInvalid.map((l) => l.rowNum));
    commit((row) => (ids.has(row.rowNum) ? fn(row) : row));
  };

  const applyCountryToRows = (rowNums, country) => {
    const ids = new Set(rowNums);
    commit((row) => (ids.has(row.rowNum) ? { ...row, phoneCountry: country } : row));
  };

  const applyBestToSelected = () => {
    if (selectedWithSuggestion.length === 0) {
      toast.error("None of the selected rows has a suggested country");
      return;
    }
    const best = {};
    selectedWithSuggestion.forEach((l) => {
      best[l.rowNum] = suggestions[l.rowNum][0].country;
    });
    commit((row) => (best[row.rowNum] ? { ...row, phoneCountry: best[row.rowNum] } : row));
  };

  const removeSelected = () => {
    if (selectedInvalid.length === 0) {
      toast.error("Select one or more invalid rows first");
      return;
    }
    commit(null, new Set(selectedInvalid.map((l) => l.rowNum)));
  };

  const tabBtn = (key, label, count, color) => (
    <button
      type="button"
      onClick={() => switchTab(key)}
      disabled={count === 0 && key === "invalid"}
      className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        activeTab === key
          ? `${color} border-current`
          : "text-gray-500 dark:text-slate-400 border-transparent hover:text-gray-700 dark:hover:text-slate-200"
      }`}
    >
      {label} <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-slate-700">{count}</span>
    </button>
  );

  return (
    <div className="space-y-3">
      <div className="flex border-b border-gray-200 dark:border-slate-700">
        {tabBtn("invalid", "Invalid leads", invalidRows.length, "text-red-600 dark:text-red-400")}
        {tabBtn("valid", "Valid leads", validRows.length, "text-green-600 dark:text-green-400")}
      </div>

      {activeTab === "invalid" && (
        <div className="bg-gray-50 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700 rounded-lg p-2 sm:p-3 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-slate-200 cursor-pointer mr-auto">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                className="w-4 h-4 rounded border-gray-300 text-primary-600"
              />
              Select all ({selectedInvalid.length}/{invalidRows.length} selected)
            </label>
            <button
              type="button"
              onClick={() => commit()}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium bg-primary-600 text-white hover:bg-primary-700"
              title="Apply your edits and check the rows again"
            >
              <HiOutlineRefresh className="w-4 h-4" />
              Re-validate{draftCount > 0 ? ` (${draftCount} edited)` : ""}
            </button>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <SearchableSelect
              className="w-full sm:w-64"
              placeholder="Set phone country…"
              searchPlaceholder="Search country or code (e.g. UAE, 971)"
              options={PHONE_COUNTRY_OPTIONS}
              value={bulkCountry}
              onChange={(e) => setBulkCountry(e.target.value)}
            />
            <button
              type="button"
              disabled={!bulkCountry}
              onClick={() => bulkApply((row) => ({ ...row, phoneCountry: bulkCountry }))}
              className="px-3 py-2 rounded-lg text-xs sm:text-sm font-medium border border-primary-300 text-primary-700 bg-white dark:bg-slate-800 dark:text-primary-300 hover:bg-primary-50 disabled:opacity-50"
            >
              Apply country to selected
            </button>
            <button
              type="button"
              onClick={() => bulkApply((row) => ({ ...row, rawAlternatePhone: "", alternatePhone: "" }))}
              className="px-3 py-2 rounded-lg text-xs sm:text-sm font-medium border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700"
            >
              Clear alternate phone
            </button>
            <button
              type="button"
              onClick={() => bulkApply((row) => ({ ...row, email: "" }))}
              className="px-3 py-2 rounded-lg text-xs sm:text-sm font-medium border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700"
            >
              Clear email
            </button>
            <button
              type="button"
              onClick={removeSelected}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium border border-red-300 text-red-600 bg-white dark:bg-slate-800 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <HiOutlineTrash className="w-4 h-4" />
              Remove selected
            </button>
          </div>
          {suggestionGroups.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-lg p-2 sm:p-3">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="flex items-center gap-1 text-xs sm:text-sm font-semibold text-amber-800 dark:text-amber-300 mr-auto">
                  <HiOutlineLightBulb className="w-4 h-4" />
                  Suggested countries for {Object.keys(suggestions).length} invalid number(s)
                </span>
                <button
                  type="button"
                  disabled={selectedWithSuggestion.length === 0}
                  onClick={applyBestToSelected}
                  className="px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                  title="Apply each selected row's best-match country"
                >
                  Apply best match to selected ({selectedWithSuggestion.length})
                </button>
              </div>
              <div className="space-y-1.5">
                {suggestionGroups.slice(0, 6).map((g) => (
                  <div
                    key={g.country}
                    className="flex flex-wrap items-center gap-2 text-xs sm:text-sm bg-white dark:bg-slate-800 rounded-md px-2.5 py-1.5 border border-amber-100 dark:border-slate-700"
                  >
                    <span className="font-medium text-gray-800 dark:text-slate-100">{g.label}</span>
                    <span className="text-gray-500 dark:text-slate-400">
                      — {g.rowNums.length} {g.rowNums.length > 1 ? "rows" : "row"}, e.g. {g.formatted}
                    </span>
                    <button
                      type="button"
                      onClick={() => applyCountryToRows(g.rowNums, g.country)}
                      className="ml-auto px-2.5 py-1 rounded-md text-xs font-medium border border-amber-400 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-500/20"
                    >
                      Apply to {g.rowNums.length} {g.rowNums.length > 1 ? "rows" : "row"}
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-amber-700/80 dark:text-amber-300/70 mt-1.5">
                Detected from the digit pattern. A number can fit more than one country, so check before applying.
              </p>
            </div>
          )}

          <p className="text-xs text-gray-500 dark:text-slate-400">
            Edit the highlighted fields below, or select rows and use a bulk action. Click Re-validate to move fixed rows to Valid.
          </p>
        </div>
      )}

      <div className="max-h-[28rem] overflow-y-auto space-y-2 pr-1">
        {list.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-6">No leads here</p>
        )}

        {list.slice(0, shown).map((lead) =>
          activeTab === "invalid" ? (
            <div
              key={lead.rowNum}
              className="p-2 sm:p-3 rounded-lg border bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-900/40"
            >
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={selected.has(lead.rowNum)}
                  onChange={() => toggleRow(lead.rowNum)}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600"
                />
                <HiOutlineX className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span className="font-medium text-gray-900 dark:text-slate-100 text-xs sm:text-sm">
                  Row {lead.rowNum}
                </span>
                {lead.phoneCountry && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-primary-100 text-primary-700">
                    Country: {lead.phoneCountry}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {EDIT_FIELDS.map((field) => {
                  const draft = drafts[lead.rowNum];
                  const value = draft && field.key in draft ? draft[field.key] : field.get(lead);
                  const bad = fieldHasError(lead, field);
                  return (
                    <label key={field.key} className="block">
                      <span className="block text-[11px] font-medium text-gray-500 dark:text-slate-400 mb-0.5">
                        {field.label}
                      </span>
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => setDraft(lead.rowNum, field.key, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            commit();
                          }
                        }}
                        className={`w-full px-2.5 py-1.5 rounded-md border text-xs sm:text-sm bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500/30 ${
                          bad ? "border-red-400" : "border-gray-200 dark:border-slate-600"
                        }`}
                      />
                    </label>
                  );
                })}
              </div>
              <div className="mt-2 space-y-0.5">
                {lead.errors.map((error, idx) => (
                  <p key={idx} className="text-xs text-red-600 dark:text-red-400">{error}</p>
                ))}
                {(lead.warnings || []).map((warning, idx) => (
                  <p key={`w${idx}`} className="text-xs text-amber-600">{warning}</p>
                ))}
              </div>
              {suggestions[lead.rowNum] && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] font-medium text-gray-600 dark:text-slate-300">Looks like:</span>
                  {suggestions[lead.rowNum].map((sug, idx) => (
                    <button
                      key={sug.country}
                      type="button"
                      onClick={() => applyCountryToRows([lead.rowNum], sug.country)}
                      className={`px-2 py-1 rounded-md text-[11px] sm:text-xs border transition-colors ${
                        idx === 0
                          ? "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-300"
                          : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200"
                      }`}
                      title={`Use ${sug.label} for this row`}
                    >
                      {idx === 0 && <span className="font-semibold">Best: </span>}
                      {sug.label} · {sug.formatted}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div
              key={lead.rowNum}
              className="flex items-start gap-2 p-2 sm:p-3 rounded-lg border bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-900/40"
            >
              <HiOutlineCheck className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <span className="font-medium text-gray-900 dark:text-slate-100 text-xs sm:text-sm truncate block">
                  Row {lead.rowNum}: {lead.name}
                </span>
                <div className="text-xs text-gray-500 dark:text-slate-400 flex flex-wrap gap-x-2">
                  <span>{lead.phone}</span>
                  {lead.alternatePhone && <span>• Alt: {lead.alternatePhone}</span>}
                  {lead.email && <span>• {lead.email}</span>}
                  {lead.stage && <span className="text-purple-600">• {lead.stage}</span>}
                  {lead.status && <span className="text-blue-600">• {lead.status}</span>}
                  {lead.priority && <span className="text-orange-600">• {lead.priority}</span>}
                  {lead.source && <span className="text-teal-600">• {lead.source}</span>}
                </div>
                {(lead.warnings || []).map((warning, idx) => (
                  <p key={idx} className="text-xs text-amber-600">{warning}</p>
                ))}
              </div>
              <button
                type="button"
                onClick={() => commit(null, new Set([lead.rowNum]))}
                className="p-1 rounded-md text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 flex-shrink-0"
                title="Remove this lead from the import"
              >
                <HiOutlineTrash className="w-4 h-4" />
              </button>
            </div>
          ),
        )}

        {list.length > shown && (
          <button
            type="button"
            onClick={() => setShown((n) => n + PAGE)}
            className="w-full py-2 text-xs sm:text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            Show more ({list.length - shown} remaining)
          </button>
        )}
      </div>
    </div>
  );
};

export default ImportPreview;
